import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Direction } from '../../constants/Direction';
import { useTable } from '../../contexts/table/TableContext';
import { TableColumn } from '../../models/table/TableColumn';
import { TableData } from "../../models/table/TableData";
import { TableRowGroup } from '../../models/table/TableRowGroup';
import OutsideClick from '../outside-click/outside-click';
import TableBody from './table-body/table-body';
import TableHead from './table-head/table-head';
import TableMenu from './table-menu/table-menu';

type Props = {
    className?: string,
    data: TableData[],
    groupedData?: TableRowGroup[],
    columns: TableColumn[],
    childTableHeading?: string,
    highlightExpandedParent?: boolean,
    selectRows?: boolean,
    customise?: boolean,
    onSelectionUpdated?: (id: string[]) => void,
    onSort?: (id: number, direction: string) => void,
    selectedIds?: string[],
    onRowClicked?: (rowData: any) => void,
    customHighlightRow?: any
    tableId?: string
    customRowColourWithRange?: any
    rowPath?: string,
    isStriped?: boolean
    cyTableName?: string
}

const Table: FC<Props> = ({
    rowPath,
    className = '',
    data = [],
    groupedData = [],
    columns = [],
    childTableHeading,
    highlightExpandedParent,
    selectRows = false,
    customise = false,
    onSelectionUpdated = ids => { },
    selectedIds = [],
    onSort = id => { },
    onRowClicked,
    customHighlightRow,
    tableId,
    cyTableName,
    customRowColourWithRange,
    isStriped
}) => {

    // --> start context
    const tableContext = useTable();

    // The plan was a reference object, but it seems we need to reassign completely to correctly rerender the component.
    useEffect(() => {
        if (tableContext.tableDescriptor) {
            const newColumnDefinitions = tableContext.tableDescriptor;
            setColumnDefinitions(newColumnDefinitions);
        }
    }, [tableContext.isInUse && tableContext.tableDescriptor])

    const [showMenu, setShowMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const [allSelected, setAllSelected] = useState(false);

    const handleOpenMenu = (event?: MouseEvent<HTMLButtonElement>) => {
        const menuWidth = 400;
        const buttonRect = event?.currentTarget?.getBoundingClientRect();
        if (buttonRect) {
            const viewportPadding = 8;
            const maxLeft = window.innerWidth - menuWidth - viewportPadding;
            const preferredLeft = buttonRect.right - menuWidth;

            setMenuPosition({
                top: buttonRect.bottom + 6,
                left: Math.max(viewportPadding, Math.min(preferredLeft, maxLeft)),
            });
        } else if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.top + 36,
                left: Math.max(8, rect.right - menuWidth),
            });
        }
        setShowMenu(true);
    };
    const [rowData, setRowData] = useState<any[]>(new Array(0));
    const [columnDefinitions, setColumnDefinitions] = useState<TableColumn[]>(new Array(0));

    const handleSelect = (rowIndex) => {
        onSelectionUpdated([rowData[rowIndex].data.id]);
    };

    const handleCustomiseSubmit = (newColumns) => {
        setShowMenu(false);
        tableContext.updateTableDescriptor(newColumns);
    };

    useEffect(() => {
        if (data) {
            const newData = data.map(item => ({ data: { ...item }, selected: [...selectedIds, ...tableContext.alwaysSelectedAndDisabled!].indexOf('' + item.id) === -1 ? false : true }));
            setRowData(newData);
        }
    }, [data]);

    useEffect(() => {
        if (columns) {
            const newColDefs = columns.map((item, colIndex) => ({ ...item, colIndex, sort: Direction.ascending }));
            setColumnDefinitions([...newColDefs]);
        }
    }, [columns]);


    useEffect(() => {
        setRowData(rowData => rowData.map(item => {
            return ({ ...item, selected: allSelected });
        }));
        const rowDataIdsToFlip = rowData.filter(item => {
            return ((allSelected && selectedIds.indexOf(item.data.id) < 0)
                || (!allSelected && selectedIds.indexOf(item.data.id) !== -1));
        }).map(item => item.data.id);
        onSelectionUpdated(rowDataIdsToFlip);
    }, [allSelected])

    useEffect(() => {
        const newData = rowData => rowData.map((item, i) => ({ ...item, selected: [...selectedIds, ...tableContext.alwaysSelectedAndDisabled!].indexOf(item.data.id) !== -1 }));
        setRowData(newData);
    }, [selectedIds.length])

    return (
        <div ref={containerRef} className={`relative w-full flex ${className} `}>
            {showMenu && createPortal(
                <div style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, zIndex: 60 }}>
                    <OutsideClick onClick={() => setShowMenu(false)}>
                        <TableMenu cols={columnDefinitions.filter(col => !col.isFilterOnlyColumn)} onClose={() => setShowMenu(false)} onSubmit={handleCustomiseSubmit} />
                    </OutsideClick>
                </div>,
                document.body
            )}
            <table className="w-full" id={tableId ?? 'tableId'} data-cy={`${cyTableName}Table`}>
                <TableHead
                    columnDefinitions={columnDefinitions}
                    onSort={onSort}
                    selectAll={selectRows}
                    onSelectAll={() => setAllSelected(!allSelected)}
                    allSelected={allSelected}
                    customise={customise}
                    onCustomise={handleOpenMenu} />
                <TableBody
                    columnDefinitions={columnDefinitions}
                    childTableHeading={childTableHeading}
                    highlightExpandedParent={highlightExpandedParent}
                    rows={rowData}
                    groupedRows={groupedData}
                    onSelect={handleSelect}
                    selectRows={selectRows}
                    onRowClicked={onRowClicked}
                    customHighlightRow={customHighlightRow}
                    tableId={tableId}
                    customRowColourWithRange={customRowColourWithRange}
                    rowPath={rowPath}
                    isStriped={isStriped} />
            </table>
        </div>
    );
}

export default Table;
